import React, { FormEvent, useState, useEffect, useRef } from 'react';
import sellerService from '../services/sellerService';
import { useAppContext } from '../context/AppContext';

interface EditStoreProfileCompProps {
    show: boolean;
    onClose: () => void;
}

interface ProfileFormErrors {
    sellerName?: string;
    sellerDescription?: string;
}

const EditStoreProfileComp: React.FC<EditStoreProfileCompProps> = ({ show, onClose }) => {

    const modalRef = useRef<HTMLDivElement | null>(null);
    const bsModalRef = useRef<any>(null);

    const { seller, setSeller } = useAppContext();
    const [sellerName, setSellerName] = useState(seller!.sellerName);
    const [sellerDescription, setSellerDescription] = useState(seller!.sellerDescription);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [profileFormErrors, setProfileFormErrors] = useState<ProfileFormErrors>({});
    const [serviceStatus, setServiceStatus] = useState({ type: '', message: '' });

    useEffect(() => {
        if (modalRef.current) {
            bsModalRef.current = new (window as any).bootstrap.Modal(modalRef.current);
            modalRef.current.addEventListener('hidden.bs.modal', onClose);
        }
        return () => {
            modalRef.current?.removeEventListener('hidden.bs.modal', onClose);
        };
    }, []);

    useEffect(() => {
        if (show) {
            setSellerName(seller!.sellerName);
            setSellerDescription(seller!.sellerDescription);
            bsModalRef.current?.show();
        } else {
            bsModalRef.current?.hide();
        }
    }, [show]);

    const validateSellerProfile = () => {
        let errors: ProfileFormErrors = {};
        if (!sellerName) {
            errors.sellerName = "Store Name is required";
        }

        if(sellerName && sellerName.length > 100) {
            errors.sellerName = "Store Name must be less than 100 characters";
        }

        if (!sellerDescription) {
            errors.sellerDescription = "Store description is required";
        }
        
        if(sellerDescription && sellerDescription.length > 1000) {
            errors.sellerDescription = "Store description must be less than 1000 characters";
        }
        setProfileFormErrors(errors);
        return Object.keys(errors).length === 0;
    }

    const handleProfileSubmit = (e: FormEvent) => {
        const updateSellerProfile = async () => {
            try {
                const updateSucceed = await sellerService.updateSellerProfile(sellerName, sellerDescription);
                if (updateSucceed === "Success") {
                    setSeller({ ...seller!, sellerName: sellerName, sellerDescription: sellerDescription });
                    onClose();
                } else {
                    setServiceStatus({ type: "Error", message: "Failed to update store profile." })
                }
            } catch (error) {
                setServiceStatus({ type: "Error", message: "Failed to update store profile." })
            }
            setIsSubmitting(false);
        }
        e.preventDefault();
        setServiceStatus({ type: "", message: "" })
        if (validateSellerProfile()) {
            setIsSubmitting(true);
            updateSellerProfile();
        }
    }

    return (
        <div className="modal fade" tabIndex={-1} ref={modalRef} data-bs-backdrop="static" data-bs-keyboard="false" >
            <div className="modal-dialog">
                <div className="modal-content rounded-0">
                    <div className="modal-header">
                        <h5 className="modal-title">Edit Profile</h5>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => bsModalRef.current?.hide()}
                        ></button>
                    </div>
                    <div className="modal-body">
                        <div className='row g-3'>
                            <div className="col-md-12">
                                <label className="form-label">Store Name</label>
                                <input
                                    type="text" maxLength={100}
                                    className={`form-control ${profileFormErrors.sellerName ? 'is-invalid' : ''}`}
                                    value={sellerName}
                                    onChange={(e) => setSellerName(e.target.value)}
                                />
                                {profileFormErrors.sellerName && <div className="invalid-feedback">{profileFormErrors.sellerName}</div>}
                            </div>
                            <div className="col-md-12">
                                <label className="form-label">Description</label>
                                <textarea className={`form-control ${profileFormErrors.sellerDescription ? 'is-invalid' : ''}`} rows={5} value={sellerDescription} onChange={(e) => setSellerDescription(e.target.value)}  maxLength={1000}></textarea>
                                {profileFormErrors.sellerDescription && <div className="invalid-feedback">{profileFormErrors.sellerDescription}</div>}
                            </div>
                            {serviceStatus.type === "Error" && <div className="col-md-12"><div className="col-md-12 clearfix"><div className="alert alert-danger p-2 mb-0">{serviceStatus.message}</div></div></div>}
                        </div>
                    </div>
                    <div className="modal-footer d-flex justify-content-between">
                        <button className="btn btn-outline-dark rounded-0" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-dark rounded-0" onClick={handleProfileSubmit} disabled={isSubmitting}>
                            {isSubmitting ? (<><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Saving ...</>) : ("Save")}
                        </button>
                    </div>
                </div>
            </div >
        </div >
    );
};

export default EditStoreProfileComp;
